import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_CALL_LOGS } from '@ingenico/util-i18n-config';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreAlert } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreCodeBlock } from '@ingenico/react-ui/core-code-block';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { SearchBarInput } from '@ingenico/react-ui/search-bar-input';
import { CoreChip } from '@ingenico/react-ui/core-chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { FormControl, InputLabel, LinearProgress, ListSubheader, MenuItem, Select } from '@mui/material';
import { Box } from '@mui/system';
import { enumLogicalDeviceRole } from '@ingenico/api-sdk-device-management-private-v2';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceManagementCapability } from '../../hooks';
import type { ParameterOutputFileModalProps } from './ParameterOutputFileModal.model';
import styles from './ParameterOutputFileModal.module.scss';
import { useParameterOutputFileModal } from './useParameterOutputFileModal.hook';

export function ParameterOutputFileModal({
  deploymentUnitId,
  deviceId,
  configurationId,
  moduleItemField,
  onCancel,
  onBack,
  isOpen,
}: ParameterOutputFileModalProps): ReactNode {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_CALL_LOGS));
  const isDmMultiMerchantEnabled = useDeviceManagementCapability('deployments.isDmMultiMerchantEnabled');

  const {
    isSuccess,
    isError,
    errorMessage,
    code,
    isLoading,
    isLoadingCode,
    language,
    moduleName,
    moduleVersion,
    devices,
    visibleDevices,
    deviceSearch,
    onDeviceSearchChange,
    onSelectDevice,
    onSelectOutputFilename,
    onSelectTemplate,
    outputFilename,
    outputFiles,
    selectedOutputFilename,
    selectedDeviceIndex,
    selectedTemplateId,
    templates,
    downloadOutputFile,
  } = useParameterOutputFileModal({
    deploymentUnitId,
    deviceId,
    configurationId,
    moduleItemField,
    isOpen,
  });

  return (
    <BasicDialogModal
      isOpen={isOpen}
      hasCloseIcon
      title={t('sections.parameterOutputFile.modal.title', { moduleName })}
      subTitle={moduleVersion}
      onIconClick={onCancel}
      maxWidth="md"
      actions={
        <div className={styles.modalActions}>
          <CoreButton variant="text" onClick={onBack} startIcon={<ArrowBackIcon />}>
            {t('sections.parameterOutputFile.modal.button.back', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
          </CoreButton>
          <CoreButton
            variant="contained"
            endIcon={<DownloadOutlinedIcon />}
            onClick={downloadOutputFile}
            loading={isLoadingCode}
            disabled={!isSuccess}
          >
            {t('sections.parameterOutputFile.modal.button.submit', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
          </CoreButton>
        </div>
      }
      customPaperClassName={styles.paperContainer}
    >
      <Box className={classNames(styles.containerBox)}>
        {isError && (
          <div>
            <CoreAlert
              severity={'Error'}
              title={t('sections.parameterOutput.error.title', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
              message={errorMessage}
            />
          </div>
        )}
        {isLoading && <LinearProgress />}

        {isSuccess && !isError && (
          <Box className={classNames(styles.selectsBox)}>
            <FormControl size="small" className={classNames(styles.formContainer)}>
              <InputLabel id="label-template-select-id">
                {t('sections.parameterOutputFile.modal.label.template', {
                  ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                  count: templates?.length ?? 0,
                })}
              </InputLabel>

              <Select
                id="template-select"
                labelId="label-template-select-id"
                label={t('sections.parameterOutputFile.modal.label.template', {
                  ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                  count: templates?.length ?? 0,
                })}
                value={selectedTemplateId}
                disabled={templates?.length <= 1}
                onChange={(evt) => {
                  onSelectTemplate(evt.target.value);
                }}
              >
                {templates?.map((element) => (
                  <MenuItem key={element.templateId} value={element.templateId}>
                    {element.templateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isDmMultiMerchantEnabled && devices.length > 1 && (
              <FormControl size="small" className={classNames(styles.formContainer)}>
                <InputLabel id="label-device-select-id">
                  {t('sections.parameterOutputFile.modal.label.device', {
                    ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                    count: devices.length,
                  })}
                </InputLabel>
                <Select
                  id="device-select"
                  labelId="label-device-select-id"
                  label={t('sections.parameterOutputFile.modal.label.device', {
                    ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                    count: devices.length,
                  })}
                  value={selectedDeviceIndex}
                  onChange={(evt) => {
                    onSelectDevice(Number(evt.target.value));
                  }}
                  onClose={() => {
                    onDeviceSearchChange('');
                  }}
                  MenuProps={{ classes: { paper: styles.devicePickerPaper } }}
                >
                  <ListSubheader className={styles.deviceSearch}>
                    <SearchBarInput
                      autoFocus
                      fullWidth
                      size="small"
                      variant="filled"
                      value={deviceSearch}
                      placeholder={t('search.placeholder', { ns: ALL_LNGS_NAMESPACES.COMMON })}
                      onSearchChange={onDeviceSearchChange}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  </ListSubheader>
                  {visibleDevices.map(({ device, index }) => (
                    <MenuItem key={device.logicalDeviceId ?? index} value={index}>
                      <Box className={styles.deviceOption}>
                        {device.logicalDeviceName ?? device.logicalDeviceId}
                        {device.logicalDeviceRoleSnapshot === enumLogicalDeviceRole.PRIMARY && (
                          <CoreChip
                            label={t('sections.parameterOutputFile.modal.label.primary', {
                              ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                            })}
                            color="primary"
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" className={classNames(styles.formContainer, styles.fileOutputFormContainer)}>
              <InputLabel id="label-file-output-id">
                {t('sections.parameterOutputFile.modal.label.fileOutput', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS })}
              </InputLabel>
              <Select
                id="file-output-select"
                label={t('sections.parameterOutputFile.modal.label.fileOutput', {
                  ns: ALL_LNGS_NAMESPACES.CALL_LOGS,
                })}
                labelId="label-file-output-id"
                value={selectedOutputFilename}
                disabled={outputFiles?.length <= 1}
                renderValue={() => (
                  <CoreTypography
                    customClassName={styles.fileName}
                    showTooltip
                    tooltipTitle={selectedOutputFilename}
                    truncateText
                    variant={CoreTypographyVariant.Body02Regular}
                  >
                    {selectedOutputFilename}
                  </CoreTypography>
                )}
                onChange={(evt) => {
                  onSelectOutputFilename(evt.target.value);
                }}
              >
                {outputFiles?.map(({ fileName }) => (
                  <MenuItem key={fileName} value={fileName}>
                    <CoreTypography
                      customClassName={styles.fileName}
                      showTooltip
                      tooltipTitle={fileName}
                      truncateText
                      variant={CoreTypographyVariant.Body02Regular}
                    >
                      {fileName}
                    </CoreTypography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {isLoadingCode && <LinearProgress />}
        {isSuccess && (
          <div className={styles.codeBlockContainer}>
            <CoreCodeBlock title={outputFilename} language={language} code={code} isCopy wrapLongLines />
          </div>
        )}
      </Box>
    </BasicDialogModal>
  );
}
